import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { filter, map, startWith } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DataService } from '../../services/data.service';
import { SessionService } from '../../services/session.service';
import { LoaderService } from '../../services/ui/loader.service';
import { ToastService } from '../../services/ui/toast.service';
import { ScannerService } from '../../services/scanner.service';
import { scanDestination } from '../../services/scan-routing';
import { Scan } from '../../services/barcode';
import { buildGarageMenu, garageLandingPath } from '../../services/nav.service';
import { url } from '../../config/app.constants';
import { consoleVersion } from '../../config/version';
import { licenseExpiryNotice } from '@parkingless/interface';
import { AmountToggleComponent } from './amount-toggle.component';
import { LicenseBannerComponent } from './license-banner.component';
import { NotificationBellComponent } from './notification-bell.component';

/**
 * The signed-in console shell: garage-scoped sidebar, toolbar and routed content.
 *
 * The sidebar is built from the user's garages at runtime rather than from a static
 * array, because which garages exist — and which menu each role sees — is per user.
 */
@Component({
  selector: 'app-full',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatMenuModule,
    MatProgressBarModule,
    AmountToggleComponent,
    NotificationBellComponent,
    LicenseBannerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './full.component.html',
})
export class FullComponent {
  private readonly data = inject(DataService);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly scanner = inject(ScannerService);
  private readonly toast = inject(ToastService);
  protected readonly loader = inject(LoaderService);

  protected readonly user$ = this.data.user$;
  protected readonly navGroups = toSignal(
    // Recomputed whenever the user's garages or roles change.
    this.data.garageRoles$.pipe(),
    { initialValue: null },
  );
  /**
   * The shell is responsive from here.
   *
   * The sidenav used to be `mode="side"` and permanently open: on a tablet — which is
   * what sits on a garage desk — a 260px drawer pushed the content off to one side with
   * no way to reclaim the width except the hamburger, and even then the drawer displaced
   * the page instead of covering it. Below the breakpoint it now overlays and starts
   * closed, which is the standard Material behaviour.
   */
  private readonly handset = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 960px)')
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  protected readonly sidenavMode = computed<'over' | 'side'>(() =>
    this.handset() ? 'over' : 'side',
  );

  /** Set only by the toggle; `null` means "follow the viewport". */
  private readonly sidenavOverride = signal<boolean | null>(null);

  protected readonly sidenavOpen = computed(() => this.sidenavOverride() ?? !this.handset());

  protected readonly userProfilePath = `/${url.userProfile.path}`;
  protected readonly garageAdminPath = `/${url.garageAdmin.path}`;
  protected readonly supportPath = `/${url.support.path}`;
  /** Shown under the support link, so a report can say which build it came from. */
  protected readonly consoleVersion = consoleVersion;

  /**
   * The URL, as a signal, so the toolbar can tell which garage is on screen.
   *
   * Read from the router rather than from an ActivatedRoute snapshot because the shell
   * sits above the routed component: its own snapshot never carries the `:id` of the
   * child that is actually rendering.
   */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** `/parking/{id}/{page}` → the id, or null anywhere else in the console. */
  private readonly routedGarageId = computed(() => {
    const match = /^\/parking\/([^/?#]+)/.exec(this.currentUrl());
    return match ? match[1] : null;
  });

  /** The sub-page currently open, so switching garage keeps you on the same screen. */
  private readonly currentSubPage = computed(() => {
    const match = /^\/parking\/[^/?#]+\/([^/?#]+)/.exec(this.currentUrl());
    return match ? match[1] : null;
  });

  /**
   * The last garage the URL named, remembered across the routes that name none.
   *
   * The URL stays authoritative — this is only consulted on `/garage/admin`,
   * `/user/profile` and `/support`, which are garage-less but still want a populated
   * sidebar rather than one that empties out and refills as you pass through them.
   * Seeded from storage so a fresh load of one of those routes has it too.
   */
  private readonly rememberedGarageId = signal<string | null>(readRememberedGarage());

  constructor() {
    // Writing it here rather than in the computed keeps `selectedEntry` free of side
    // effects; a computed that wrote to storage would do so on every read.
    effect(() => {
      const garageId = this.routedGarageId();
      if (garageId) {
        this.rememberedGarageId.set(garageId);
        writeRememberedGarage(garageId);
      }
    });

    // The barcode gun works on every page of the selected garage. A screen with its own
    // use for a code claims that kind (`bindScanner`); the rest is routed from here.
    this.scanner.start();
    inject(DestroyRef).onDestroy(() => this.scanner.stop());
    effect(() => {
      const garage = this.selectedGarage();
      if (garage) {
        this.scanner.usePrefix(garage.scannerPrefix);
      }
    });
    this.scanner.unclaimed$.pipe(takeUntilDestroyed()).subscribe((scan) => this.routeScan(scan));
  }

  /** Sends a scan the page on display did not take to where it belongs. */
  private routeScan(scan: Scan): void {
    const entry = this.selectedEntry();
    const destination = entry?.garage ? scanDestination(scan, entry.garage, entry.role) : null;
    if (!destination) {
      return;
    }
    if ('toast' in destination) {
      this.toast.koNotification(destination.toast);

      return;
    }
    void this.router.navigate([...destination.commands], { queryParams: destination.queryParams });
  }

  /**
   * The garage the console is currently scoped to.
   *
   * Resolved against the user's own garages rather than trusted from the URL or storage,
   * so a link to a garage they no longer have a role in — or one deleted since it was
   * remembered — selects nothing instead of rendering a menu that 403s on every item.
   */
  protected readonly selectedEntry = computed(() => {
    const roles = this.navGroups();
    if (!roles?.length) {
      return null;
    }

    const wanted = this.routedGarageId() ?? this.rememberedGarageId();

    return roles.find((entry) => entry.garage?.garageId === wanted) ?? null;
  });

  protected readonly selectedGarage = computed(() => this.selectedEntry()?.garage ?? null);

  private readonly user = toSignal(this.data.user$, { initialValue: null });
  private readonly ownLicense = toSignal(this.data.license$, { initialValue: null });

  /**
   * The warning for a licence in its last days: the one covering the garage on screen —
   * its owner's, for staff — and the user's own where no garage is selected.
   */
  protected readonly licenseNotice = computed(() =>
    licenseExpiryNotice(this.selectedEntry()?.license ?? this.ownLicense(), this.user()?.userId),
  );

  protected readonly onPurchasePage = computed(() =>
    this.currentUrl().startsWith(`/${url.buyLicense.path}`),
  );

  /** The sidebar: the pages of the selected garage, and nothing else. */
  protected readonly menu = computed(() => buildGarageMenu(this.selectedEntry()));

  protected readonly hasGarages = computed(() => Boolean(this.navGroups()?.length));

  /**
   * The garages the header dropdown offers, each linking to the equivalent page rather
   * than to its dashboard — comparing the same month across two garages is the reason
   * anyone opens this menu, and being dropped on a dashboard every time defeats it.
   *
   * Falls back to the garage's landing page when the current screen has no equivalent
   * for that role, or when there is no current screen because we are not in a garage.
   */
  protected readonly garageOptions = computed(() =>
    (this.navGroups() ?? [])
      .filter((entry) => entry.garage?.garageId)
      .map((entry) => {
        const subPage = this.currentSubPage();
        const equivalent = buildGarageMenu(entry).find((item) => item.path.endsWith(`/${subPage}`));

        return {
          garageId: entry.garage.garageId,
          name: entry.garage.name ?? '',
          link: equivalent?.path ?? garageLandingPath(entry),
        };
      }),
  );

  protected toggleSidenav(): void {
    const open = this.sidenavOpen();
    this.sidenavOverride.set(!open);
  }

  /** The backdrop or Escape closed the overlay drawer. */
  protected onSidenavClosed(): void {
    if (this.handset()) {
      this.sidenavOverride.set(false);
    }
  }

  protected async signOut(): Promise<void> {
    await this.session.signOut();
    await this.router.navigate([url.login.path]);
  }
}

/** The selected garage survives a reload; a private window simply has no memory. */
const REMEMBERED_GARAGE_KEY = 'parkingless.selectedGarage';

function readRememberedGarage(): string | null {
  try {
    return localStorage.getItem(REMEMBERED_GARAGE_KEY);
  } catch {
    return null;
  }
}

function writeRememberedGarage(garageId: string): void {
  try {
    localStorage.setItem(REMEMBERED_GARAGE_KEY, garageId);
  } catch {
    // Storage can be unavailable or full; remembering the garage is a convenience.
  }
}
